import { createContext, useCallback, useEffect, useReducer } from 'react';
import snackbarStore from '../../../../stores/Snackbar.store';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { SubmitMessage } from '../../types/chat.types';
import {
  ChatSocketContextProps,
  ChatSocketContextProviderProps,
  ConnectToChatRoomParams,
  DisconnectFromChatRoomParams,
  EmitMessageParams,
  SubmitMessageParams,
} from './chatSocket.conext.types';
import { WebSocketChatEvents } from './constants/webSocketChatEvents.constant';
import useChatWebsocket from './hooks/useChatWebsocket/useChatWebsocket';
import {
  ClientSocket,
  ErrorInMessagePayload,
  ExceptionPayload,
  NewChatCreatedPayload,
  ReceiveMessagePayload,
  ReceiveNotificationPayload,
  SendMessageInput,
  SuccessSendingMessagePayload,
  UserRemovedFromChatPayload,
} from './hooks/useChatWebsocket/useChatWebsocket.types';
import { chatSocketInitialState, chatSocketReducer } from './reducer/chatSocketReducer';
import {
  addManyMessagesWithoutConfirmation,
  addMessageWithoutConfirmation,
  addNewNotification,
  addWsException,
  chatCreated,
  cleanWebsocketMessagesByChatId,
  cleanWebsocketMessagesByMessageId,
  cleanWebsocketMessagesByTempId,
  connectionRecovered,
  messageReceived,
  removeChatToFetch,
  removeChatToRemove,
  removeNotificationsByMessageIds,
  removedFromChat,
  resetState,
  updateMessageWithError,
} from './reducer/chatSocketReducer.actions';
import { Message } from './reducer/chatSocketReducer.types';

export const ChatSocketContext = createContext<ChatSocketContextProps>({
  chatIdsToFetch: [],
  chatIdsToRemove: [],
  websocketMessages: [],
  receivedNotifications: [],
  errorConnectingToWsChat: {
    hasError: false,
  },
  errorRecoveringStateAfterDisconnect: {
    count: 0,
    hasErrorRecoveringState: false,
  },
  submitMessage: () => {
    return;
  },
  connectToChatRoom: () => {
    return;
  },
  cleanMessagesByChatId: () => {
    return;
  },
  cleanMessagesByTempId: () => {
    return;
  },
  disconnectFromChatRoom: () => {
    return;
  },
  cleanMessagesByMessageId: () => {
    return;
  },
  removeChatToFetchById: () => {
    return;
  },
  removeChatToRemoveById: () => {
    return;
  },
  handleRemoveNotificationsByMessageIds: () => {
    return;
  },
});

export const ChatSocketContextProvider = ({ children }: ChatSocketContextProviderProps) => {
  const {
    isBrand,
    state: { user },
  } = useApplicationContext();

  const socket = useChatWebsocket();

  const [state, dispatch] = useReducer(chatSocketReducer, chatSocketInitialState);

  const emitMessage = useCallback(
    (params: EmitMessageParams) => {
      socket?.emit(WebSocketChatEvents.SEND_MESSAGE, {
        type: params.type,
        data: params.data,
        chatId: params.chatId,
        message: params.message,
        temporaryId: params.temporaryId,
        brandIdFromChat: params.brandIdFromChat,
      });
    },
    [socket],
  );

  const submitMessage = useCallback(
    ({ chat, message }: SubmitMessageParams) => {
      if (!user) {
        return;
      }

      dispatch(
        addMessageWithoutConfirmation({
          chat,
          user: user,
          submitMessage: message,
        }),
      );
      emitMessage({
        chatId: chat.id,
        type: message.type,
        data: message.data,
        message: message.body,
        temporaryId: message.tempId,
        brandIdFromChat: chat.brand.id,
      });
    },
    [emitMessage, user],
  );

  const cleanMessagesByTempId = useCallback(
    (tempId) => dispatch(cleanWebsocketMessagesByTempId({ tempId })),
    [],
  );

  const cleanMessagesByMessageId = useCallback(
    (messageId) => dispatch(cleanWebsocketMessagesByMessageId({ messageId })),
    [],
  );

  const cleanMessagesByChatId = useCallback(
    (chatId) => dispatch(cleanWebsocketMessagesByChatId({ chatId })),
    [],
  );

  const removeChatToFetchById = useCallback(
    (chatId: string) => dispatch(removeChatToFetch({ chatId })),
    [],
  );

  const removeChatToRemoveById = useCallback(
    (chatId: string) => dispatch(removeChatToRemove({ chatId })),
    [],
  );

  const connectToChatRoom = useCallback(
    ({ brandId, chatId }: ConnectToChatRoomParams) => {
      socket?.emit(WebSocketChatEvents.JOIN_CHAT, { brandId, chatId });
    },
    [socket],
  );

  const disconnectFromChatRoom = useCallback(
    ({ brandId, chatId }: DisconnectFromChatRoomParams) => {
      socket?.emit(WebSocketChatEvents.DISCONNECT_FROM_CHAT, { chatId, brandId });
    },
    [socket],
  );

  const handleExceptionEvent = (payload: ExceptionPayload) => {
    dispatch(addWsException(payload));
  };

  const handleSuccessSendingMessageEvent = (payload: SuccessSendingMessagePayload) => {
    const newMessage: Message = {
      ...payload.message,
      error: false,
      tempId: payload.temporaryId,
      chatId: payload.message.chat.id,
    };
    dispatch(messageReceived({ message: newMessage }));
  };

  const handleErrorInMessageEvent = (payload: ErrorInMessagePayload) => {
    if (payload.reason) {
      snackbarStore.handleMessage({ message: payload.reason, type: 'error' });
    }
    dispatch(updateMessageWithError({ tempId: payload.temporaryId }));
  };

  const handleBroadcastMessageEvent = (payload: ReceiveMessagePayload) => {
    const newMessage: Message = {
      ...payload.message,
      error: false,
      chatId: payload.message.chat.id,
    };
    dispatch(messageReceived({ message: newMessage }));
  };

  const handleReceiveNotificationEvent = (payload: ReceiveNotificationPayload) => {
    dispatch(
      addNewNotification({
        lastNotification: {
          type: payload.type,
          chatId: payload.chatId,
          message: payload.message,
          messageId: payload.messageId,
          updatedAt: payload.updatedAt,
          authorName: payload.authorName,
          authorEmail: payload.authorEmail,
        },
      }),
    );
  };

  const handleReceiveTemporaryMessageEvent = (payload: SendMessageInput) => {
    if (!user) {
      return;
    }

    const messageFromAnotherUserSocket: SubmitMessage = {
      type: payload.type,
      data: payload.data,
      body: payload.message,
      chatId: payload.chatId,
      tempId: payload.temporaryId,
    };
    dispatch(
      addMessageWithoutConfirmation({
        user: user,
        submitMessage: messageFromAnotherUserSocket,
      }),
    );
  };

  const handleReceiveManyTemporaryMessagesEvent = () => (payload: SendMessageInput[]) => {
    if (!user) {
      return;
    }

    dispatch(
      addManyMessagesWithoutConfirmation({
        user: user,
        submitMessages: payload.map<SubmitMessage>((message) => ({
          type: message.type,
          data: message.data,
          body: message.message,
          chatId: message.chatId,
          tempId: message.temporaryId,
        })),
      }),
    );
  };

  const handleRemoveNotificationsByMessageIds = useCallback((messageIds: string[]) => {
    dispatch(removeNotificationsByMessageIds({ messageIds }));
  }, []);

  const handleNewChatCreatedEvent = (payload: NewChatCreatedPayload) => {
    dispatch(chatCreated({ chatId: payload.newChatId }));
  };

  const handleUserRemovedFromChatEvent = (payload: UserRemovedFromChatPayload) => {
    dispatch(removedFromChat({ chatId: payload.chatId }));
  };

  const joinDefaultRooms = (clientSocket: ClientSocket) => {
    if (isBrand) {
      clientSocket.emit(WebSocketChatEvents.CONNECT_BRAND_NOTIFICATIONS);
    } else {
      clientSocket.emit(WebSocketChatEvents.CONNECT_STORE_NOTIFICATIONS);
    }
  };

  const handleConnectEvent = () => {
    if (!socket) {
      return;
    }

    joinDefaultRooms(socket);
  };

  const handleReconnectEvent = () => {
    if (!socket) {
      return;
    }

    if (!socket.recovered) {
      dispatch(connectionRecovered({ recoveredSocketState: false }));
      joinDefaultRooms(socket);
    } else {
      dispatch(connectionRecovered({ recoveredSocketState: true }));
    }
  };

  const disconnectSocket = () => {
    socket?.removeAllListeners();
    dispatch(resetState());
  };

  useEffect(() => {
    if (!socket) {
      return;
    }

    socket.on('connect', handleConnectEvent);
    socket.io.on('reconnect', handleReconnectEvent);
    socket.on(WebSocketChatEvents.EXCEPTION, handleExceptionEvent);
    socket.on(WebSocketChatEvents.ERROR_IN_MESSAGE, handleErrorInMessageEvent);
    socket.on(WebSocketChatEvents.NEW_CHAT_CREATED, handleNewChatCreatedEvent);
    socket.on(WebSocketChatEvents.BROADCAST_MESSAGE, handleBroadcastMessageEvent);
    socket.on(WebSocketChatEvents.RECEIVE_NOTIFICATION, handleReceiveNotificationEvent);
    socket.on(WebSocketChatEvents.USER_REMOVED_FROM_CHAT, handleUserRemovedFromChatEvent);
    socket.on(WebSocketChatEvents.SUCCESS_SENDING_MESSAGE, handleSuccessSendingMessageEvent);
    socket.on(WebSocketChatEvents.RECEIVE_TEMPORARY_MESSAGE, handleReceiveTemporaryMessageEvent);
    socket.on(
      WebSocketChatEvents.RECEIVE_MANY_TEMPORARY_MESSAGES,
      handleReceiveManyTemporaryMessagesEvent,
    );

    return disconnectSocket;
  }, [socket]);

  const values: ChatSocketContextProps = {
    submitMessage,
    connectToChatRoom,
    disconnectFromChatRoom,
    cleanMessagesByChatId,
    cleanMessagesByTempId,
    cleanMessagesByMessageId,
    removeChatToFetchById,
    removeChatToRemoveById,
    handleRemoveNotificationsByMessageIds,
    wsException: state.wsException,
    chatIdsToFetch: state.chatIdsToFetch,
    chatIdsToRemove: state.chatIdsToRemove,
    websocketMessages: state.websocketMessages,
    receivedNotifications: state.receivedNotifications,
    errorConnectingToWsChat: state.errorConnectingToWsChat,
    errorRecoveringStateAfterDisconnect: state.errorRecoveringStateAfterDisconnect,
    lastWebsocketMessage: state.websocketMessages[state.websocketMessages.length - 1],
  };

  return <ChatSocketContext.Provider value={values}>{children}</ChatSocketContext.Provider>;
};
